/* eslint-disable @typescript-eslint/naming-convention, @next/next/no-img-element */
import { Box } from '@packages/shared/src/components/Box/Box';
import { Tooltip } from '@packages/modules/src/Tooltip/Tooltip';
import { useGetTooltipContent } from '../../hooks/useGetTooltipContent';
import { CmsImageItem } from './CmsImageItem';
import { registerComponents } from '../ComponentRecognizer/componentRegistry';
import { ComponentTypes } from '../../../interfaces';

import type { CmsImageType } from './types';
import { styleJustifyContent } from './utils/styleHelpers';

type CmsImageProps = {
  data: CmsImageType;
};

/**
 * CmsImage component to render image content fetched from the CMS.
 *
 * @param data containing image data and optional fields.
 * @returns JSX.Element A Box component containing the i mage or a linked image.
 */
export const CmsImage = ({ data }: CmsImageProps) => {
  const {
    field_margin: fieldMargin = '0',
    field_distance: distanceType,
    field_alignment: Align = 'left',
    field_custom_id: dynamicYieldId,
    promotion_item: tooltipDataPromotion,
    content_snippet_bundled: tooltipDataContentSnippet,
    behavior_paragraph_tooltip_activation: tooltipActivation,
    behavior_paragraph_tooltip_positioning: tooltipPositioning,
    id,
  } = data;

  const tooltipContent = useGetTooltipContent(tooltipDataPromotion, tooltipDataContentSnippet);

  return (
    <Box
      key={`image-${id}`}
      className="image"
      {...(dynamicYieldId && { 'data-dy': dynamicYieldId })}
      component="span"
      sx={{
        display: 'flex',
        ...(Align && {
          justifyContent: styleJustifyContent(Align),
        }),
        ...(distanceType === 'variation-1' && {
          paddingInline: { xs: 3, lg: 0 },
        }),
      }}
    >
      <Box
        key={`innerImage-${id}`}
        component="span"
        sx={{
          display: 'flex',
          position: 'relative',
          width: '100%',
          ...(fieldMargin !== '' && {
            margin: fieldMargin,
          }),
          ...(Align && {
            justifyContent: styleJustifyContent(Align),
          }),
        }}
      >
        {tooltipContent && !!tooltipActivation ? (
          <Tooltip
            content={tooltipContent}
            positioning={tooltipPositioning}
            key={`cmsImageItemTooltip-${data.id}`}
          >
            <CmsImageItem data={data} key={`cmsImageItemWithTooltip-${data.id}`} />
          </Tooltip>
        ) : (
          <CmsImageItem data={data} key={`cmsImageItem-${data.id}`} />
        )}
      </Box>
    </Box>
  );
};

// Needed to run this component in Storybook and Jest tests. Register the component in a global componentRegistry object
registerComponents({
  [ComponentTypes.SvgFile]: CmsImage,
  [ComponentTypes.ImageSimple]: CmsImage,
  [ComponentTypes.ImageSimpleParagraph]: CmsImage,
});
